(() => {
  const GLOBAL_KEY = '__jdHeaderController';
  const HEADER_SELECTOR = '.shopify-section-header';
  const MENU_SELECTOR = '.list-menu--inline';
  const MENU_ITEM_SELECTOR = '.list-menu-item';
  const MEGA_MENU_SELECTOR = '.mega-menu';
  const TABBED_MEGA_MENU_SELECTOR = '.mega-menu--tabs';
  const TAB_WRAPPER_SELECTOR = '.mega-menu__tab-wrapper';
  const VISIBLE_CLASS = 'list-menu--megamenu-visible';
  const ACTIVE_TAB_CLASS = 'mega-menu__tab-wrapper--active';
  const CLOSE_DURATION = 300;

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].init();
    return;
  }

  const cleanupByHeader = new WeakMap();
  let announcementObserver = null;
  let observedAnnouncement = null;
  let predictiveSearchLoadPromise;

  const containsPredictiveSearch = (root = document) =>
    root?.matches?.('predictive-search') ||
    root?.querySelector?.('predictive-search');

  const loadPredictiveSearch = (root = document) => {
    if (!containsPredictiveSearch(root)) return Promise.resolve(false);
    if (window.customElements?.get('predictive-search')) {
      return Promise.resolve(true);
    }
    if (predictiveSearchLoadPromise) return predictiveSearchLoadPromise;

    const startLoading = () => {
      if (window.customElements?.get('predictive-search')) {
        return Promise.resolve(true);
      }
      if (predictiveSearchLoadPromise) return predictiveSearchLoadPromise;

      const assets = window.themeAssets;
      if (!assets?.loadScript) {
        console.error(
          'The theme asset loader is unavailable for predictive search.',
        );
        return Promise.resolve(false);
      }

      const coreReady = assets.loadCore?.() || Promise.resolve();
      predictiveSearchLoadPromise = Promise.resolve(coreReady)
        .then(() => assets.loadScript('search-form.js'))
        .then(() => assets.loadScript('predictive-search.js'))
        .then(() => true)
        .catch((error) => {
          predictiveSearchLoadPromise = undefined;
          console.error('Predictive search could not be initialized.', error);
          return false;
        });

      return predictiveSearchLoadPromise;
    };

    if (window.themeAssets?.loadScript) return startLoading();

    return new Promise((resolve) => {
      window.addEventListener(
        'theme:assets:ready',
        () => resolve(startLoading()),
        { once: true },
      );
    });
  };

  const getHeaders = (root = document) => {
    const headers = [];

    const closestHeader = root.closest?.(HEADER_SELECTOR);
    if (closestHeader) headers.push(closestHeader);

    if (root.matches?.(HEADER_SELECTOR) && !headers.includes(root)) {
      headers.push(root);
    }

    root.querySelectorAll?.(HEADER_SELECTOR).forEach((header) => {
      if (!headers.includes(header)) headers.push(header);
    });

    return headers;
  };

  const activateMegaMenuTab = (tabWrapper) => {
    const tabbedMegaMenu = tabWrapper.closest(TABBED_MEGA_MENU_SELECTOR);
    if (!tabbedMegaMenu) return;

    tabbedMegaMenu.querySelectorAll(TAB_WRAPPER_SELECTOR).forEach((wrapper) => {
      wrapper.classList.toggle(ACTIVE_TAB_CLASS, wrapper === tabWrapper);
    });
  };

  const initializeMegaMenuTabs = (header, register) => {
    header.querySelectorAll(TABBED_MEGA_MENU_SELECTOR).forEach((megaMenu) => {
      const tabWrappers = Array.from(
        megaMenu.querySelectorAll(TAB_WRAPPER_SELECTOR),
      );

      if (!tabWrappers.length) return;

      const activeTab = tabWrappers.find((wrapper) =>
        wrapper.classList.contains(ACTIVE_TAB_CLASS),
      );

      activateMegaMenuTab(activeTab || tabWrappers[0]);

      tabWrappers.forEach((wrapper) => {
        register(wrapper, 'mouseenter', () => {
          activateMegaMenuTab(wrapper);
        });

        register(wrapper, 'focusin', () => {
          activateMegaMenuTab(wrapper);
        });
      });
    });
  };

  const refreshAnnouncementObserver = () => {
    const announcement = document.querySelector('.section-announcement');

    if (announcement === observedAnnouncement) return;

    announcementObserver?.disconnect();
    announcementObserver = null;
    observedAnnouncement = announcement;

    if (!announcement) {
      document.documentElement.style.setProperty('--ann-height', '0px');
      return;
    }

    if (typeof IntersectionObserver !== 'function') {
      document.documentElement.style.setProperty(
        '--ann-height',
        `${announcement.getBoundingClientRect().height}px`,
      );
      return;
    }

    announcementObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      const height = entry?.isIntersecting ? entry.intersectionRect.height : 0;

      document.documentElement.style.setProperty(
        '--ann-height',
        `${height}px`,
      );
    });

    announcementObserver.observe(announcement);
  };

  const initializeHeader = (header) => {
    if (!header || cleanupByHeader.has(header)) return;

    const cleanupCallbacks = [];
    const closeTimers = new WeakMap();
    const suppressedFocusItems = new WeakSet();
    const register = (element, eventName, handler, options) => {
      if (!element) return;

      element.addEventListener(eventName, handler, options);
      cleanupCallbacks.push(() => {
        element.removeEventListener(eventName, handler, options);
      });
    };

    const menu = header.querySelector(MENU_SELECTOR);
    const search = header.querySelector('.header__search');
    const menuItems = menu
      ? Array.from(menu.children).filter((child) =>
          child.matches(MENU_ITEM_SELECTOR),
        )
      : [];

    const headerElement = header.querySelector('.header');
    const updateHeaderMetrics = () => {
      if (!headerElement) return;

      document.documentElement.style.setProperty(
        '--header-height',
        `${header.getBoundingClientRect().height}px`,
      );
      document.documentElement.style.setProperty(
        '--margin-header',
        getComputedStyle(headerElement).getPropertyValue('margin-top'),
      );
    };

    const updateNestedSubmenus = (event) => {
      const menuItem = event.target.closest?.(MENU_ITEM_SELECTOR);
      if (!menuItem || !header.contains(menuItem)) return;

      menuItem
        .querySelectorAll('.header__submenu .header__submenu')
        .forEach((submenu) => {
          submenu.style.setProperty(
            '--clientY',
            `${submenu.getBoundingClientRect().top}px`,
          );
        });
    };

    updateHeaderMetrics();
    register(header, 'mouseover', updateNestedSubmenus);
    register(header, 'focusin', updateNestedSubmenus);

    if (typeof ResizeObserver === 'function') {
      const metricsObserver = new ResizeObserver(updateHeaderMetrics);
      metricsObserver.observe(header);
      cleanupCallbacks.push(() => metricsObserver.disconnect());
    } else {
      register(window, 'resize', updateHeaderMetrics, { passive: true });
    }

    const getMegaMenu = (menuItem) =>
      menuItem?.querySelector(MEGA_MENU_SELECTOR) || null;

    const clearCloseTimer = (megaMenu) => {
      const timer = closeTimers.get(megaMenu);
      if (!timer) return;

      window.clearTimeout(timer);
      closeTimers.delete(megaMenu);
    };

    const hideMegaMenu = (menuItem, immediate = false) => {
      const megaMenu = getMegaMenu(menuItem);
      if (!megaMenu) return;

      clearCloseTimer(megaMenu);
      menuItem.classList.remove(VISIBLE_CLASS);
      megaMenu.style.opacity = '0';

      const finish = () => {
        if (menuItem.classList.contains(VISIBLE_CLASS)) return;

        megaMenu.style.display = 'none';
        megaMenu.style.right = 'auto';
        closeTimers.delete(megaMenu);
      };

      if (immediate) {
        finish();
        return;
      }

      closeTimers.set(megaMenu, window.setTimeout(finish, CLOSE_DURATION));
    };

    const hideAllMegaMenus = (except = null, immediate = false) => {
      menuItems.forEach((menuItem) => {
        if (menuItem !== except) hideMegaMenu(menuItem, immediate);
      });
    };

    const showMegaMenu = (menuItem) => {
      const megaMenu = getMegaMenu(menuItem);
      if (!megaMenu) {
        hideAllMegaMenus(null, true);
        return;
      }

      hideAllMegaMenus(menuItem, true);
      clearCloseTimer(megaMenu);

      menuItem.classList.add(VISIBLE_CLASS);
      megaMenu.style.display = 'grid';
      megaMenu.style.opacity = '0';
      megaMenu.style.right = 'auto';

      if (megaMenu.getBoundingClientRect().right > window.innerWidth) {
        megaMenu.style.right = '0';
      }

      const reveal = () => {
        if (menuItem.classList.contains(VISIBLE_CLASS)) {
          megaMenu.style.opacity = '1';
        }
      };

      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(reveal);
      } else {
        window.setTimeout(reveal, 0);
      }
    };

    register(header, 'keydown', (event) => {
      if (event.key !== 'Escape' && event.code !== 'Escape') return;

      if (
        search &&
        search.isOpen &&
        typeof search.close === 'function'
      ) {
        search.close();
      }

      const activeMegaMenu = event.target.closest?.(MEGA_MENU_SELECTOR);
      const activeMenuItem = activeMegaMenu?.closest(MENU_ITEM_SELECTOR);
      const trigger = activeMenuItem?.querySelector(
        ':scope > .header__menu-item',
      );

      hideAllMegaMenus(null, true);

      if (activeMenuItem && trigger) {
        suppressedFocusItems.add(activeMenuItem);
        trigger.focus();
        suppressedFocusItems.delete(activeMenuItem);
      }
    });

    menuItems.forEach((menuItem) => {
      register(menuItem, 'mouseenter', () => {
        if (menuItem.classList.contains('list-menu--megamenu')) {
          showMegaMenu(menuItem);
        } else {
          hideAllMegaMenus(null, true);
        }
      });

      register(menuItem, 'mouseleave', () => {
        if (menuItem.classList.contains('list-menu--megamenu')) {
          hideMegaMenu(menuItem);
        }
      });

      register(menuItem, 'focusin', () => {
        if (suppressedFocusItems.has(menuItem)) return;

        if (menuItem.classList.contains('list-menu--megamenu')) {
          showMegaMenu(menuItem);
        } else {
          hideAllMegaMenus(null, true);
        }
      });
    });

    register(menu, 'mouseleave', () => {
      hideAllMegaMenus();
    });

    register(menu, 'focusout', () => {
      window.setTimeout(() => {
        if (!menu.contains(document.activeElement)) {
          hideAllMegaMenus();
        }
      }, 0);
    });

    const menuCloseButton = header.querySelector(
      '.header__modal-close-button',
    );
    const mobileMenuDrawer = header.querySelector('header-drawer');

    register(menuCloseButton, 'click', (event) => {
      if (typeof mobileMenuDrawer?.closeMenuDrawer === 'function') {
        mobileMenuDrawer.closeMenuDrawer(event);
      }
    });

    initializeMegaMenuTabs(header, register);

    cleanupByHeader.set(header, () => {
      cleanupCallbacks.forEach((cleanup) => cleanup());
      hideAllMegaMenus(null, true);
      cleanupByHeader.delete(header);
    });
  };

  const init = (root = document) => {
    getHeaders(root).forEach(initializeHeader);
    refreshAnnouncementObserver();
    void loadPredictiveSearch(root);
  };

  const destroy = (root) => {
    getHeaders(root).forEach((header) => {
      cleanupByHeader.get(header)?.();
    });

    window.setTimeout(refreshAnnouncementObserver, 0);
  };

  const controller = { init, destroy };
  window[GLOBAL_KEY] = controller;

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target);
  });

  document.addEventListener('shopify:section:reorder', (event) => {
    init(event.target);
  });

  init();
})();
